const API_URL = import.meta.env.VITE_API_URL;
const printPerLoad = 10;

export const getMainImageKey = (element) => `${element.procid}-main`;

export const getPlateImageKey = (element) => `${element.procid}-plate`;

export const getMainImageUrl = (element, qualityParam) => `${element.archiver}${element.storagevolumeid}/${element.laneid}/${element.containerid}/image/${element.bestimage}${qualityParam}`;

export const getPlateImageUrl = (element) => `${element.archiver}${element.storagevolumeid}/${element.laneid}/${element.containerid}/image/${element.bestimage}?plateArea=true`;

export const getPrintImagePlaceholder = () => `${API_URL}/public/assets/img/sentry.imgnotfound.large.748x480.png`;

export const getPreparedImageSource = (preparedImageSources, key) => preparedImageSources[key] || getPrintImagePlaceholder();

export const getPrintImageEntries = (items, qualityParam) => items.flatMap(element => {
    const entries = [{
        key: getMainImageKey(element),
        url: getMainImageUrl(element, qualityParam),
        main: true
    }];

    if (element.plate) {
        entries.push({
            key: getPlateImageKey(element),
            url: getPlateImageUrl(element),
            main: false
        });
    }

    return entries;
});

export const getPrintImageKeys = (items) => items.flatMap(element => {
    const keys = [getMainImageKey(element)];

    if (element.plate) keys.push(getPlateImageKey(element));

    return keys;
});

export const removePreparedImageSources = ({id, setPreparedImageSources, setOnErrorImage}) => {
    setPreparedImageSources(sources => {
        const updatedSources = {...sources};
        delete updatedSources[`${id}-main`];
        delete updatedSources[`${id}-plate`];
        return updatedSources;
    });

    setOnErrorImage(errors => {
        const updatedErrors = {...errors};
        delete updatedErrors[`${id}-main`];
        delete updatedErrors[`${id}-plate`];
        return updatedErrors;
    });
};

export const revokeImageObjectUrls = (imageObjectUrlsRef) => {
    Object.values(imageObjectUrlsRef.current).forEach(url => URL.revokeObjectURL(url));
    imageObjectUrlsRef.current = {};
};

const loadImageElement = (url) => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(url);
    image.onerror = reject;
    image.src = url;
});

export const preparePrintImages = ({items, showImages, qualityParam, imageObjectUrlsRef, isLoadingImagesRef, setPreparedImageSources, setOnErrorImage, preparedImageSourcesRef, preservePreparedImages = false, onComplete}) => {
    if (!preservePreparedImages) {
        revokeImageObjectUrls(imageObjectUrlsRef);
        setPreparedImageSources({});
        setOnErrorImage({});
    }

    if (!showImages || items.length === 0) {
        isLoadingImagesRef.current = false;
        onComplete?.();
        return undefined;
    }

    let cancelled = false;
    let nextIndex = 0;
    let pendingSources = {};
    let flushTimeout = null;
    isLoadingImagesRef.current = true;

    const preparedSources = preservePreparedImages ? preparedImageSourcesRef.current : {};
    const imageEntries = getPrintImageEntries(items, qualityParam).filter(entry => !preparedSources[entry.key]);

    if (imageEntries.length === 0) {
        isLoadingImagesRef.current = false;
        onComplete?.();
        return undefined;
    }

    const flushPreparedSources = () => {
        flushTimeout = null;
        if (cancelled || Object.keys(pendingSources).length === 0) return;

        const sources = pendingSources;
        pendingSources = {};

        setPreparedImageSources(prev => ({...prev, ...sources}));
    };

    const queuePreparedSource = (key, source) => {
        pendingSources[key] = source;
        if (!flushTimeout) flushTimeout = setTimeout(flushPreparedSources, 100);
    };

    const loadPreparedImage = async ({key, url, main}) => {
        try {
            const response = await fetch(url, {cache: "force-cache", credentials: "include"});
            if (!response.ok) throw new Error("Imagem indisponível");

            const blob = await response.blob();
            if (cancelled) return;

            const objectUrl = URL.createObjectURL(blob);
            if (imageObjectUrlsRef.current[key]) URL.revokeObjectURL(imageObjectUrlsRef.current[key]);

            imageObjectUrlsRef.current[key] = objectUrl;
            queuePreparedSource(key, objectUrl);
        } catch {
            try {
                await loadImageElement(url);
                if (!cancelled) queuePreparedSource(key, url);
            } catch {
                if (cancelled) return;

                if (main) setOnErrorImage(prev => ({...prev, [key]: true}));
                queuePreparedSource(key, getPrintImagePlaceholder());
            }
        }
    };

    const loadQueue = async () => {
        while (!cancelled) {
            const entry = imageEntries[nextIndex];
            nextIndex += 1;

            if (!entry) return;
            await loadPreparedImage(entry);
        }
    };

    const workers = Array.from({length: Math.min(printPerLoad, imageEntries.length)}, loadQueue);

    Promise.all(workers).then(() => {
        if (cancelled) return;
        isLoadingImagesRef.current = false;

        if (flushTimeout) {
            clearTimeout(flushTimeout);
            flushPreparedSources();
        }

        onComplete?.();
    });

    return () => {
        cancelled = true;
        isLoadingImagesRef.current = false;
        if (flushTimeout) clearTimeout(flushTimeout);
    };
};